import {
  Card,
  CardBody,
  Link,
  Modal,
  ModalBody,
  ModalContent,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { answerToChartLabel } from '@/patient-app-common/helpers/progress_helper';

import { EntryList } from './EntryList';
import { ImgList } from './ImgList';
import { LineChartModal } from './line/LineChartModal';
import { LinePreview } from './line/LinePreview';
import { MultiLineChartModal } from './multiline/MultiLineChartModal';
import { MultiLinePreview } from './multiline/MultiLinePreview';
import { WeeklyTableData } from './table/WeeklyTableData';
import { TablePreview } from './table/TablePreview';

export function ChartPreview({ chartQuestions }) {
  return (
    <div className="flex flex-col gap-4">
      {chartQuestions.map((question) => {
        if (!question.patientEntries.length) return null;
        switch (question.type) {
          case 'camera':
            return <ImgSummary question={question} key={question.title} />;
          case 'checkbox':
          case 'radio':
            return <TableSummary question={question} key={question.title} />;
          case 'gen_scale':
          case 'numerical_input':
          case 'picker':
            return (
              <LineChartSummary question={question} key={question.title} />
            );
          case 'multiple_numerical_input':
            return (
              <MultiLineChartSummary question={question} key={question.title} />
            );
          case 'text':
            return <TextSummary question={question} key={question.title} />;
          default:
            return null;
        }
      })}
    </div>
  );
}

function TableSummary({ question }) {
  const { t } = useTranslation('progress');
  const [modal, toggleModal] = useState(false);
  const lastEntry = _.last(question.patientEntries);
  const { date } = lastEntry;

  return (
    <>
      <Modal
        isOpen={modal}
        onClose={() => toggleModal(false)}
        scrollBehavior="inside"
        size="lg"
      >
        <ModalContent>
          <ModalBody>
            <WeeklyTableData question={question} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Card key={question.id}>
        <CardBody>
          <h3 className="font-semibold text-lg">{question.chartTitle}</h3>
          <div className="mx-auto my-4 w-full">
            <TablePreview question={question} />
          </div>
          <p className="text-sm">
            {t('chartslist.date', { fromNow: dayjs(date).fromNow() })}
          </p>
          <Link
            className="font-light text-sm"
            href="#"
            onPress={() => toggleModal(true)}
          >
            {t('common.link.all_entries')}
            <Icon icon="mdi:chevron-right" className="inline-flex" />
          </Link>
        </CardBody>
      </Card>
    </>
  );
}

function LineChartSummary({ question }) {
  const { t } = useTranslation('progress');
  const [modal, toggleModal] = useState(false);
  const lastEntry = _.last(question.patientEntries);
  const { answer, date } = lastEntry;
  const { unit } = question;
  const parsedAnswer = answerToChartLabel(question, answer);

  return (
    <>
      <Modal
        isOpen={modal}
        onClose={() => toggleModal(false)}
        scrollBehavior="inside"
        size="lg"
      >
        <ModalContent>
          <ModalBody>
            <LineChartModal question={question} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Card key={question.id}>
        <CardBody>
          <h3 className="font-semibold text-lg">{question.chartTitle}</h3>
          <div className="grid grid-cols-2">
            <div>
              <p className="font-light text-lg py-4">
                {parsedAnswer} {unit}
              </p>
              <p className="text-sm">
                {t('chartslist.date', { fromNow: dayjs(date).fromNow() })}
              </p>
              <Link
                className="font-light text-sm"
                href="#"
                onPress={() => toggleModal(true)}
              >
                {t('common.link.all_entries')}
                <Icon icon="mdi:chevron-right" className="inline-flex" />
              </Link>
            </div>
            <div>
              <LinePreview question={question} />
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

function MultiLineChartSummary({ question }) {
  const { t } = useTranslation('progress');
  const [modal, toggleModal] = useState(false);
  const lastEntry = _.last(question.patientEntries);
  const { answer, date } = lastEntry;
  const { unit } = question;
  const parsedAnswer = answerToChartLabel(question, answer);

  return (
    <>
      <Modal
        isOpen={modal}
        onClose={() => toggleModal(false)}
        scrollBehavior="inside"
        size="lg"
      >
        <ModalContent>
          <ModalBody>
            <MultiLineChartModal question={question} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Card key={question.id}>
        <CardBody>
          <h3 className="font-semibold text-lg">{question.chartTitle}</h3>
          <div className="grid grid-cols-2">
            <div>
              <p className="font-light text-lg py-4">
                {parsedAnswer} {unit}
              </p>
              <p className="text-sm">
                {t('chartslist.date', { fromNow: dayjs(date).fromNow() })}
              </p>
              <Link
                className="font-light text-sm"
                onPress={() => toggleModal(true)}
              >
                {t('common.link.all_entries')}
                <Icon icon="mdi:chevron-right" className="inline-flex" />
              </Link>
            </div>
            <div>
              <MultiLinePreview question={question} />
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

function TextSummary({ question }) {
  const { t } = useTranslation('progress');
  const [modal, toggleModal] = useState(false);
  const entriesWithText = _.filter(question.patientEntries, 'answer');
  if (!entriesWithText.length) return null;
  const { answer, date } = _.last(entriesWithText);

  return (
    <>
      <Modal
        isOpen={modal}
        onClose={() => toggleModal(false)}
        scrollBehavior="inside"
        size="lg"
      >
        <ModalContent>
          <ModalBody>
            <EntryList question={question} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Card key={question.id}>
        <CardBody>
          <h3 className="font-semibold text-lg">{question.chartTitle}</h3>
          <p className="font-light my-4 whitespace-pre-wrap break-words">
            {answer}
          </p>
          <p className="text-sm">
            {t('chartslist.date', { fromNow: dayjs(date).fromNow() })}
          </p>
          <Link
            className="font-light text-sm"
            href="#"
            onPress={() => toggleModal(true)}
          >
            {t('common.link.all_entries')}
            <Icon icon="mdi:chevron-right" className="inline-flex" />
          </Link>
        </CardBody>
      </Card>
    </>
  );
}

function ImgSummary({ question }) {
  const { t } = useTranslation('progress');
  const [modal, toggleModal] = useState(false);
  const entriesWithImg = _.filter(
    question.patientEntries,
    ({ answer }) => !_.isEmpty(answer)
  );
  if (!entriesWithImg.length) return null;
  const { answer, date } = _.last(entriesWithImg);

  return (
    <>
      <Modal
        isOpen={modal}
        onClose={() => toggleModal(false)}
        scrollBehavior="inside"
        size="lg"
      >
        <ModalContent>
          <ModalBody>
            <ImgList question={question} />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Card key={question.id}>
        <CardBody>
          <h3 className="font-semibold text-lg">{question.chartTitle}</h3>
          <figure className="grid grid-cols-2">
            <div className="flex flex-col justify-end">
              <figcaption className="text-sm">
                {t('chartslist.date', { fromNow: dayjs(date).fromNow() })}
              </figcaption>
              <Link
                className="font-light text-sm"
                href="#"
                onPress={() => toggleModal(true)}
              >
                {t('common.link.all_entries')}
                <Icon icon="mdi:chevron-right" className="inline-flex" />
              </Link>
            </div>
            <div className="flex justify-end">
              <img src={answer} style={{ height: '100px' }} />
            </div>
          </figure>
        </CardBody>
      </Card>
    </>
  );
}
